import {map, switchMap} from 'rxjs/operators';
import {of} from 'rxjs/internal/observable/of';

import {
    ApiError,
    ApiResponse,
    ServerResponse,
    ServerResponseData,
    ClientStatusesEnum,
    HttpStatusCodesEnum,
    UserRelationTypesEnum,
    ClientUserStatusesEnum,
    PermissionApplicationsEnum,
    UsersService,
    TradingService,
    ReportsService,
    IndividualsService,
    PermissionsService,
    CompanyEmployeesService,
    IndividualSubordinatesService,
    PermissionInterface,
    IndividualInterface,
    ClientUserReportInterface,
    UserWithPermissionsInterface,
    TradingAccountAccessInterface,
    IndividualModel,
    PermissionModel,
    ClientUserReportModel,
    ClientUserFilterModel,
    UserWithPermissionsModel,
    TradingAccountAccessModel,
    SupervisedUsersCollection
} from '@spa/api';

import {Helper} from '../../../shared/classes';
import {Pagination} from '../../../shared/components/pagination';

import {ClientAccessRightsResolverErrorStatesEnum} from '../enums';
import {ClientAccessRightsResolverDataInterface} from '../interfaces';
import {forkJoin} from "rxjs";

export abstract class ClientAccessRightsResolver {
    protected constructor(
        protected usersService: UsersService,
        protected tradingService: TradingService,
        protected permissionsService: PermissionsService,
        protected individualsService: IndividualsService,
        protected individualSubordinatesService: IndividualSubordinatesService,
        protected companyEmployeesService: CompanyEmployeesService,
        protected reportsService: ReportsService
    ) {
    }

    private getClientUserFilter(userId: number): ClientUserFilterModel {
        return new ClientUserFilterModel(
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            userId,
            null,
            Pagination.defaultPagesChunk,
            1,
            ClientUserReportModel.getCustomerIdSelector(),
            false
        );
    }

    private executeGetClientRequest(
        request: ServerResponse<IndividualInterface | ClientUserReportInterface[]>,
        isClient: boolean
    ): ServerResponse<ClientAccessRightsResolverDataInterface> {
        return request.pipe(
            switchMap((getResponse: ServerResponseData<IndividualInterface | ClientUserReportInterface[]>) => {
                if (
                    (getResponse.status !== HttpStatusCodesEnum.Ok) ||
                    (getResponse.data === null) ||
                    (!isClient && (!Array.isArray(getResponse.data) || (Array.isArray(getResponse.data) && getResponse.data.length === 0)))
                ) {
                    return Helper.errorsHelper()
                        .createErrorResponseObservable<IndividualInterface | ClientUserReportInterface[]>(
                            getResponse,
                            ClientAccessRightsResolverErrorStatesEnum.GetClientFailed
                        );
                }

                const client = isClient ? new IndividualModel(getResponse.data as IndividualInterface) :
                    new ClientUserReportModel(getResponse.data[0] as ClientUserReportInterface);

                return this.getUserWithPermissions(client, isClient);
            })
        );
    }

    private getUserWithPermissions(
        clientOrClientUser: IndividualModel | ClientUserReportModel,
        isClient: boolean
    ): ServerResponse<ClientAccessRightsResolverDataInterface> {
        const userId = isClient ? (clientOrClientUser as IndividualModel).userCorrelationId :
            (clientOrClientUser as ClientUserReportModel).userId;

        return this.usersService.getPermissions(userId).pipe(
            switchMap((getUserWithPermissionsResponse: ServerResponseData<UserWithPermissionsInterface>) => {
                if (getUserWithPermissionsResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponseObservable<UserWithPermissionsInterface>(
                        getUserWithPermissionsResponse,
                        ClientAccessRightsResolverErrorStatesEnum.GetUserWithPermissionsFailed
                    );
                }

                let userWithPermissions = null;

                if (getUserWithPermissionsResponse.data !== null) {
                    userWithPermissions = new UserWithPermissionsModel(
                        getUserWithPermissionsResponse.data as UserWithPermissionsInterface
                    );
                }

                const clientOrClientUserId = isClient ? (clientOrClientUser as IndividualModel).id :
                    (clientOrClientUser as ClientUserReportModel).customerId;

                const userRelationType = isClient ? UserRelationTypesEnum.Individual :
                    (clientOrClientUser as ClientUserReportModel).userTypeRelation;

                return this.getPortalPermissions(userRelationType, userWithPermissions, clientOrClientUserId);
            })
        );
    }

    private getPortalPermissions(
        userRelationType: number,
        userWithPermissions: UserWithPermissionsModel,
        clientOrClientUserId: number,
    ): ServerResponse<ClientAccessRightsResolverDataInterface> {
        const pwpRequest = this.permissionsService.getAll(PermissionApplicationsEnum.ProWeb);
        const fpRequest = this.permissionsService.getAll(PermissionApplicationsEnum.FundPortal);

        return forkJoin([pwpRequest, fpRequest]).pipe(
            switchMap(permissionsResponses => {
                let isFailedStatus = null;
                let isInvalidData = null;
                const data:PermissionInterface[] = [];

                permissionsResponses.forEach(permissionsResponse => {
                    if (permissionsResponse.status !== HttpStatusCodesEnum.Ok) {
                        isFailedStatus = permissionsResponse;
                        return;
                    }

                    const isInvalid = Helper.errorsHelper()
                        .checkForInvalidRespondedArray<PermissionInterface>(permissionsResponse);

                    if (isInvalid) {
                        isInvalidData = permissionsResponse;
                        return;
                    }

                    data.push(...(permissionsResponse.data as PermissionInterface[]))
                });

                const portalPermissions = data.map((item: PermissionInterface) => new PermissionModel(item))
                    .sort((previous: PermissionModel, next: PermissionModel) => previous.id - next.id);

                return this.getAssignedAccountsAccessRights(
                    userRelationType,
                    userWithPermissions,
                    portalPermissions,
                    clientOrClientUserId
                );
            })
        );
    }

    private getAssignedAccountsAccessRights(
        userRelationType: number,
        userWithPermissions: UserWithPermissionsModel,
        portalPermissions: PermissionModel[],
        clientOrClientUserId: number
    ): ServerResponse<ClientAccessRightsResolverDataInterface> {
        return this.tradingService.getAccountsAccessRights(clientOrClientUserId, userRelationType).pipe(
            switchMap((getAccessRightsResponse: ServerResponseData<TradingAccountAccessInterface[]>) => {
                if (getAccessRightsResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponseObservable<TradingAccountAccessInterface[]>(
                        getAccessRightsResponse,
                        ClientAccessRightsResolverErrorStatesEnum.GetAssignedAccountsRightsFailed
                    );
                }

                let assignedAccountsAccessRights = [];

                if (!Helper.errorsHelper().checkForInvalidRespondedArray<TradingAccountAccessInterface>(getAccessRightsResponse)) {
                    const data = getAccessRightsResponse.data as TradingAccountAccessInterface[];

                    assignedAccountsAccessRights = data.map((item: TradingAccountAccessInterface) => new TradingAccountAccessModel(item));
                }

                switch (userRelationType) {
                    case UserRelationTypesEnum.Individual:
                        return Helper.createSuccessResponseObservable<ClientAccessRightsResolverDataInterface>({
                            userWithPermissions,
                            portalPermissions,
                            assignedAccountsAccessRights,
                            clientOrClientUserId,
                            userRelationType
                        });
                    case UserRelationTypesEnum.CompanyEmployee:
                    case UserRelationTypesEnum.IndividualSubordinate:
                        return this.getSupervisedUsers(
                            userWithPermissions,
                            portalPermissions,
                            assignedAccountsAccessRights,
                            clientOrClientUserId,
                            userRelationType
                        );
                    default:
                        return Helper.errorsHelper().createBadRequestResponseObservable(
                            ClientAccessRightsResolverErrorStatesEnum.GetAssignedAccountsRightsFailed
                        );
                }
            })
        );
    }

    private getSupervisedUsers(
        userWithPermissions: UserWithPermissionsModel,
        portalPermissions: PermissionModel[],
        assignedAccountsAccessRights,
        clientUserId: number,
        userRelationType: number
    ): ServerResponse<ClientAccessRightsResolverDataInterface> {
        let request = this.companyEmployeesService.getSupervisedUsers(clientUserId);

        if (userRelationType === UserRelationTypesEnum.IndividualSubordinate) {
            request = this.individualSubordinatesService.getSupervisedUsers(clientUserId);
        }

        return request.pipe(
            map((supervisedUsersResponse: ServerResponseData<SupervisedUsersCollection>) => {
                if (supervisedUsersResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<SupervisedUsersCollection>(
                        supervisedUsersResponse,
                        ClientAccessRightsResolverErrorStatesEnum.GetSupervisedRightsFailed
                    );
                }

                let supervisedUsers = null;

                if (!supervisedUsersResponse.data) {
                    supervisedUsers = new SupervisedUsersCollection([]);
                } else {
                    supervisedUsers = supervisedUsersResponse.data as SupervisedUsersCollection;
                }

                return Helper.createSuccessResponse<ClientAccessRightsResolverDataInterface>({
                    userWithPermissions,
                    portalPermissions,
                    assignedAccountsAccessRights,
                    clientOrClientUserId: clientUserId,
                    userRelationType,
                    supervisedUsers
                });
            })
        );
    }

    protected getClient(clientOrClientUserId: number): ServerResponse<ClientAccessRightsResolverDataInterface> {
        let request = null;
        let isClient = false;

        switch (this.getUserRelationType()) {
            case UserRelationTypesEnum.Individual:
                request = this.individualsService.getById(clientOrClientUserId);

                isClient = true;
                break;
            case UserRelationTypesEnum.CompanyEmployee:
            case UserRelationTypesEnum.IndividualSubordinate:
                const filter = this.getClientUserFilter(clientOrClientUserId);

                request = this.reportsService.getClientUsersReport(filter);
                break;
        }

        if (!request) {
            return Helper.errorsHelper().createBadRequestResponseObservable(ClientAccessRightsResolverErrorStatesEnum.GetClientFailed);
        }

        return this.executeGetClientRequest(request, isClient);
    }

    abstract getUserRelationType(): number;
}
